import { useAccount, useMsal, MsalAuthenticationTemplate, MsalAuthenticationResult } from "@azure/msal-react";
import { PublicClientApplication, InteractionType, InteractionStatus } from "@azure/msal-browser";
import { Client } from "@microsoft/microsoft-graph-client"
import { AuthCodeMSALBrowserAuthenticationProvider } from "@microsoft/microsoft-graph-client/authProviders/authCodeMsalBrowser"
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { makeStyles } from "@mui/styles";
import React, { useEffect, useState } from "react";
import TagManager from "react-gtm-module";
import { Route, Switch } from "react-router-dom";
import "./App.css";
import Header from "./components/Header";
import Routes from "./components/Routes";
import SessionDialog from "./components/SessionDialog";
import { Config } from "./Config";
import AppPage from "./pages/AppPage";
import ErrorPage from "./pages/ErrorPage";
import HomePage from "./pages/HomePage";
import LogoutPage from "./pages/LogoutPage";
import "./tyrian/tyrian-icons.css";

const useStyles = makeStyles({
  maintenance: { textAlign: "center" },
});

const embedWalkme = () => {
  const script = document.createElement("script");
  script.src = `/tracking/${
    Config.environment.code === "PRD" ? "prd" : "dev"
  }.js`;
  script.async = true;
  document.body.appendChild(script);
};

const ErrorComponent: React.FC<MsalAuthenticationResult> = ({ error }) => {
  return (
    <ErrorPage error={error} />
  )
}

const LoadingComponent = () =>
{  
  return (
    <p style={{ paddingTop: "100px" }}>
      <CircularProgress />
    </p>
  );
}

function App( { instance } : { instance: PublicClientApplication }) {
  const classes = useStyles();
  
  const accountInfo = useAccount();
  const { inProgress } = useMsal();
  const [graphClient, setGraphClient] = useState<Client>();
  
  useEffect(() => {
    if (accountInfo == null) {
      setGraphClient(undefined);
    }
    else {
      const authProvider = new AuthCodeMSALBrowserAuthenticationProvider(instance, {
        account: accountInfo,
        interactionType: InteractionType.Popup,
        scopes: ['User.Read'],
      });
      const graphClient = Client.initWithMiddleware({ authProvider: authProvider });      
      setGraphClient(graphClient);
      console.info(accountInfo);
    } 
  }, [instance, accountInfo]);
  
  useEffect(() => {
    embedWalkme();
    console.info(Config);
  }, []);

  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      let tagManagerArgs: any = {
        gtmId: Config.tracking.gtm,
      };
      if (accountInfo) {
        tagManagerArgs.dataLayer = {
          qual_email: accountInfo.idTokenClaims?.email,
          qual_nickname: accountInfo.idTokenClaims?.name,
          // Leaving this here if need to support groups again in the future.
          //qual_group: user[supportUserNamespace].okta_groups,
        };
      }
      TagManager.initialize(tagManagerArgs);
    }
  }, [accountInfo, inProgress]);

  if (Config.app.maintenance) {
    return (
      <Box className={classes.maintenance}>
        <img
          alt="maintenance"
          src="https://maintenance.healthequity.com/maintenance.png"
        />
      </Box>
    );
  } else {
    return (
      <div className="App">
        <SessionDialog />
        <Switch>
          <Route path={Routes.app} exact>
            <MsalAuthenticationTemplate 
              interactionType={InteractionType.Popup} 
              errorComponent={ErrorComponent} 
              loadingComponent={LoadingComponent}
            >
              <AppPage />
            </MsalAuthenticationTemplate>
          </Route>
          <Route>
            <Header graphClient={graphClient} />
            <Route path={Routes.home} exact>
              <MsalAuthenticationTemplate 
                interactionType={InteractionType.Popup} 
                errorComponent={ErrorComponent} 
                loadingComponent={LoadingComponent}
              >
                <HomePage />
              </MsalAuthenticationTemplate>
            </Route>
            <Route path={Routes.logout} exact>
              <LogoutPage />
            </Route>
          </Route>
        </Switch>
        {accountInfo && (
          <>
            <input 
              id="qual_email" 
              type="hidden" 
              defaultValue={ accountInfo.idTokenClaims === undefined ? undefined : accountInfo.idTokenClaims.email as string } />
            <input 
              id="qual_nickname" 
              type="hidden" 
              defaultValue={ accountInfo.idTokenClaims === undefined ? undefined : accountInfo.idTokenClaims.name as string } />
          </>
        )}        
      </div>
    );
  }
}

export default App;
